import { Button } from 'components'
import { useAlert } from 'providers/confirmationModal'
import { useUsersActions } from 'providers'
import { DCProgressModalV3 } from 'components/ConfirmationModals/DCProgressModalV3'
import { useCallback } from 'react'
import { noop } from 'utils'
import { DCProgressProps } from 'types'
import { BasicConfirmationModal } from 'components/ConfirmationModals/BasicConfirmationModal'

export type DCProgressBtnV3Props = {
  userId: string
  disabled?: boolean
  onDone?: (message: string) => void
  onError?: (message: string) => void
}

export const DCProgressBtnV3 = ({
  userId,
  disabled = false,
  onDone = noop,
  onError = noop,
}: DCProgressBtnV3Props) => {
  const {
    read: readUser,
    account: { updateProgress },
  } = useUsersActions()
  const {
    createAlert,
    finalizeConfirm,
    abortConfirm,
    handleCancel,
    setDisabled,
    interceptConfirm,
  } = useAlert()

  const progressUser = useCallback(
    async (lessonNumber) => {
      await updateProgress({ userId: userId, lessonNumber: lessonNumber })
      await new Promise((res, rej) => {
        setTimeout(
          () =>
            readUser({ id: userId })
              .then(() => res(true))
              .catch((error) => rej(error)),
          2000
        )
      })
    },
    [userId, updateProgress, readUser]
  )

  const setProgress = useCallback(
    (newProgress: DCProgressProps | undefined) => {
      if (newProgress === undefined) {
        setDisabled(true)
      } else {
        setDisabled(false)
        interceptConfirm(() => {
          finalizeConfirm()
          setTimeout(() => {
            createAlert({
              content: (
                <BasicConfirmationModal
                  header="Please confirm student progress adjustment"
                  body={`Your student will be moved to ${newProgress.displayName}. All skipped content will be marked as complete. Is this correct?`}
                  handleCancel={handleCancel}
                />
              ),
              confirmText: 'Confirm',
            })
            interceptConfirm(() => {
              progressUser(newProgress.lessonNumber)
                .then(() => {
                  finalizeConfirm()
                  onDone('User Progress has been updated')
                })
                .catch(() => {
                  abortConfirm('User Progress update Failed, Try Again!')
                  onError('User Progress update Failed, Try Again!')
                })
            })
          }, 10)
        })
      }
    },
    [
      setDisabled,
      interceptConfirm,
      finalizeConfirm,
      createAlert,
      handleCancel,
      progressUser,
      onDone,
      abortConfirm,
      onError,
    ]
  )

  const handleClick = () => {
    createAlert({
      content: (
        <DCProgressModalV3
          name="dcProgressV3"
          set={setProgress}
          cancel={handleCancel}
        />
      ),
      confirmText: 'Save',
    })
    setDisabled(true)
  }

  return (
    <Button
      type="button"
      variant="buttons.outline"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 50,
      }}
      disabled={disabled}
      onClick={handleClick}
    >
      <span>Adjust Digital Course position</span>
    </Button>
  )
}
